import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ArrowLeft, Save, Upload, User, FileText, CreditCard, Smartphone, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useRider, useUpdateRider } from '@/api-services/hooks/useRider';
import { RiderDTO } from '@/api-services/hooks/adminTypes';

const riderFormSchema = z.object({
  // Personal Information
  phoneNumber: z.string()
    .min(10, 'Phone number must be at least 10 digits')
    .max(20, 'Phone number must not exceed 20 digits')
    .regex(/^\+?[\d\s\-\(\)]+$/, 'Invalid phone number format. Use digits, spaces, hyphens, and parentheses only'),
  emailId: z.string().email('Invalid email address').optional().or(z.literal('')),
  fullName: z.string().min(2, 'Full name is required'),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional(),
  homeAddress: z.string().optional(),
  workAddress: z.string().optional(),
  preferredLanguage: z.string().optional(),
  profilePhotoUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // KYC Information
  kycIdType: z.string().optional(),
  kycIdNumber: z.string().optional(),
  kycDocumentPhotoUrl: z.string().refine(
    (val) => !val || val.startsWith('http') || val.startsWith('data:'),
    { message: 'Must be a valid URL or uploaded file' }
  ).optional().or(z.literal('')),

  // Payment Information
  preferredPaymentMethod: z.string().optional(),
  walletBalance: z.number().min(0).optional().or(z.literal('')),
  savedPaymentMethods: z.array(z.string()).optional(),

  // Device & App Information
  deviceToken: z.string().optional(),
  locationAccessPermission: z.boolean().optional(),

  // Additional Fields
  signupMethod: z.string().optional(),
  otpVerified: z.boolean().optional(),
  promotionalConsent: z.boolean().optional(),
  notificationPreferences: z.array(z.string()).optional(),
});

type RiderFormData = z.infer<typeof riderFormSchema>;

const EditRiderPage: React.FC = () => {
  const navigate = useNavigate();
  const { riderId } = useParams<{ riderId: string }>();
  const riderIdNum = riderId ? parseInt(riderId, 10) : 0;
  const [activeTab, setActiveTab] = useState('personal');
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, File>>({});

  // Fetch rider data
  const { data: riderData, loading: loadingRider, error: riderError } = useRider(riderIdNum, {
    onError: (error) => {
      toast.error(`Failed to load rider: ${error.message}`);
    },
  });

  const { mutate: updateRider, isLoading } = useUpdateRider({
    onSuccess: (data, variables) => {
      toast.success('Rider updated successfully!');
      navigate('/admin/riders');
    },
    onError: (error, variables) => {
      toast.error(`Failed to update rider: ${error.message}`);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    trigger,
  } = useForm<RiderFormData>({
    resolver: zodResolver(riderFormSchema),
    defaultValues: {
      otpVerified: false,
      locationAccessPermission: false,
      promotionalConsent: false,
      savedPaymentMethods: [],
      notificationPreferences: [],
    },
  });

  // Populate form when rider data is loaded
  useEffect(() => {
    if (riderData) {
      reset({
        phoneNumber: riderData.phoneNumber || '',
        emailId: riderData.emailId || '',
        fullName: riderData.fullName || '',
        dateOfBirth: riderData.dateOfBirth || '',
        gender: riderData.gender || '',
        homeAddress: riderData.homeAddress || '',
        workAddress: riderData.workAddress || '',
        preferredLanguage: riderData.preferredLanguage || '',
        profilePhotoUrl: riderData.profilePhotoUrl || '',
        kycIdType: riderData.kycIdType || '',
        kycIdNumber: riderData.kycIdNumber || '',
        kycDocumentPhotoUrl: riderData.kycDocumentPhotoUrl || '',
        preferredPaymentMethod: riderData.preferredPaymentMethod || '',
        walletBalance: riderData.walletBalance || undefined,
        savedPaymentMethods: riderData.savedPaymentMethods || [],
        deviceToken: riderData.deviceToken || '',
        locationAccessPermission: riderData.locationAccessPermission ?? false,
        signupMethod: riderData.signupMethod || '',
        otpVerified: riderData.otpVerified ?? false,
        promotionalConsent: riderData.promotionalConsent ?? false,
        notificationPreferences: riderData.notificationPreferences || [],
      });
      // Trigger validation after resetting form with data
      trigger();
    }
  }, [riderData, reset, trigger]);

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: keyof RiderFormData
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));

      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        setValue(fieldName as any, dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper function to render file upload field with URL option
  const renderDocumentField = (
    fieldName: keyof RiderFormData,
    label: string,
    placeholder: string
  ) => {
    const hasFile = uploadedFiles[fieldName as string];
    const currentValue = watch(fieldName);

    return (
      <div className="space-y-2 md:col-span-2">
        <Label>{label}</Label>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileUpload(e, fieldName)}
              className="flex-1"
            />
            {hasFile && (
              <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                {hasFile.name}
              </span>
            )}
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          <Input
            {...register(fieldName)}
            placeholder={placeholder}
            value={currentValue || ''}
          />
          {errors[fieldName] && (
            <p className="text-sm text-destructive">{errors[fieldName]?.message}</p>
          )}
          {currentValue && (
            <p className="text-xs text-muted-foreground">
              {currentValue.startsWith('data:') ? 'File uploaded' : 'URL provided'}
            </p>
          )}
        </div>
      </div>
    );
  };

  const onSubmit = (data: RiderFormData) => {
    if (!riderIdNum) {
      toast.error('Rider ID is missing');
      return;
    }

    // Clean the data before sending to API
    const riderUpdateData: RiderDTO = {
      riderId: riderIdNum,
      phoneNumber: data.phoneNumber.trim(),
      emailId: data.emailId?.trim() || undefined,
      fullName: data.fullName.trim(),
      dateOfBirth: data.dateOfBirth || undefined,
      gender: data.gender || undefined,
      homeAddress: data.homeAddress?.trim() || undefined,
      workAddress: data.workAddress?.trim() || undefined,
      preferredLanguage: data.preferredLanguage || undefined,
      profilePhotoUrl: data.profilePhotoUrl?.trim() || undefined,
      kycIdType: data.kycIdType || undefined,
      kycIdNumber: data.kycIdNumber?.trim() || undefined,
      kycDocumentPhotoUrl: data.kycDocumentPhotoUrl?.trim() || undefined,
      preferredPaymentMethod: data.preferredPaymentMethod || undefined,
      walletBalance: data.walletBalance ? Number(data.walletBalance) : undefined,
      savedPaymentMethods: data.savedPaymentMethods || undefined,
      deviceToken: data.deviceToken?.trim() || undefined,
      locationAccessPermission: data.locationAccessPermission ?? false,
      signupMethod: data.signupMethod || undefined,
      otpVerified: data.otpVerified ?? false,
      promotionalConsent: data.promotionalConsent ?? false,
      notificationPreferences: data.notificationPreferences || undefined,
      isDeletedValue: riderData?.isDeletedValue ?? false,
      status: riderData?.status || 'ACTIVE',
    };

    updateRider({ id: riderIdNum, data: riderUpdateData });
  };

  if (loadingRider) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading rider details...</p>
        </div>
      </div>
    );
  }

  if (riderError || !riderData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-destructive mb-4">
              {riderError?.message || 'Rider not found'}
            </p>
            <Button onClick={() => navigate('/admin/riders')}>Back to Riders</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/riders')}
            className="hover:bg-muted"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Edit Rider</h1>
            <p className="text-muted-foreground mt-1">Update rider information</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="personal">
              <User className="w-4 h-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger value="kyc">
              <FileText className="w-4 h-4 mr-2" />
              KYC
            </TabsTrigger>
            <TabsTrigger value="payment">
              <CreditCard className="w-4 h-4 mr-2" />
              Payment
            </TabsTrigger>
            <TabsTrigger value="additional">
              <Smartphone className="w-4 h-4 mr-2" />
              Additional
            </TabsTrigger>
          </TabsList>

          {/* Personal Information Tab */}
          <TabsContent value="personal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update the rider's personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber">Phone Number *</Label>
                    <Input
                      id="phoneNumber"
                      {...register('phoneNumber')}
                      placeholder="+1234567890"
                    />
                    {errors.phoneNumber && (
                      <p className="text-sm text-destructive">{errors.phoneNumber.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="emailId">Email</Label>
                    <Input
                      id="emailId"
                      type="email"
                      {...register('emailId')}
                      placeholder="rider@example.com"
                    />
                    {errors.emailId && (
                      <p className="text-sm text-destructive">{errors.emailId.message}</p>
                    )}
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      {...register('fullName')}
                      placeholder="John Doe"
                    />
                    {errors.fullName && (
                      <p className="text-sm text-destructive">{errors.fullName.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth">Date of Birth</Label>
                    <Input
                      id="dateOfBirth"
                      type="date"
                      {...register('dateOfBirth')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select value={watch('gender')} onValueChange={(value) => setValue('gender', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="homeAddress">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Home Address
                    </Label>
                    <Input
                      id="homeAddress"
                      {...register('homeAddress')}
                      placeholder="123 Home St, City, State, ZIP"
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="workAddress">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Work Address
                    </Label>
                    <Input
                      id="workAddress"
                      {...register('workAddress')}
                      placeholder="456 Work Ave, City, State, ZIP"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="preferredLanguage">Preferred Language</Label>
                    <Select value={watch('preferredLanguage')} onValueChange={(value) => setValue('preferredLanguage', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="hi">Hindi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {renderDocumentField(
                    'profilePhotoUrl',
                    'Profile Photo',
                    'https://example.com/photo.jpg'
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="signupMethod">Signup Method</Label>
                    <Select value={watch('signupMethod')} onValueChange={(value) => setValue('signupMethod', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select signup method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="google">Google</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="otpVerified"
                      checked={watch('otpVerified')}
                      onCheckedChange={(checked) => setValue('otpVerified', checked as boolean)}
                    />
                    <Label htmlFor="otpVerified" className="cursor-pointer">
                      OTP Verified
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>KYC Information</CardTitle>
                <CardDescription>Update Know Your Customer verification details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="kycIdType">KYC ID Type</Label>
                    <Select value={watch('kycIdType')} onValueChange={(value) => setValue('kycIdType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select ID type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="passport">Passport</SelectItem>
                        <SelectItem value="national_id">National ID</SelectItem>
                        <SelectItem value="driving_license">Driving License</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kycIdNumber">KYC ID Number</Label>
                    <Input
                      id="kycIdNumber"
                      {...register('kycIdNumber')}
                      placeholder="ID number"
                    />
                  </div>

                  {renderDocumentField(
                    'kycDocumentPhotoUrl',
                    'KYC Document Photo',
                    'https://example.com/kyc-doc.jpg'
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payment Information</CardTitle>
                <CardDescription>Update payment preferences and wallet details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="preferredPaymentMethod">Preferred Payment Method</Label>
                    <Select value={watch('preferredPaymentMethod')} onValueChange={(value) => setValue('preferredPaymentMethod', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="debit_card">Debit Card</SelectItem>
                        <SelectItem value="paypal">PayPal</SelectItem>
                        <SelectItem value="digital_wallet">Digital Wallet</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="walletBalance">Wallet Balance</Label>
                    <Input
                      id="walletBalance"
                      type="number"
                      {...register('walletBalance', { valueAsNumber: true })}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    {errors.walletBalance && (
                      <p className="text-sm text-destructive">{errors.walletBalance.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Additional Information Tab */}
          <TabsContent value="additional" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
                <CardDescription>Update device and preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="deviceToken">Device Token</Label>
                    <Input
                      id="deviceToken"
                      {...register('deviceToken')}
                      placeholder="FCM or device token"
                    />
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <Checkbox
                      id="locationAccessPermission"
                      checked={watch('locationAccessPermission')}
                      onCheckedChange={(checked) => setValue('locationAccessPermission', checked as boolean)}
                    />
                    <Label htmlFor="locationAccessPermission" className="cursor-pointer">
                      Location Access Permission Granted
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="promotionalConsent"
                      checked={watch('promotionalConsent')}
                      onCheckedChange={(checked) => setValue('promotionalConsent', checked as boolean)}
                    />
                    <Label htmlFor="promotionalConsent" className="cursor-pointer">
                      Promotional Consent
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/riders')}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-gradient-primary hover:opacity-90"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Update Rider
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditRiderPage;

